/* --------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

import _ from 'lodash';
import service from '../../services/assignments';
import scorecardService from '../../services/scorecards';

const ASSIGNMENTS_ALL = 'ASSIGNMENTS_ALL';
const ASSIGNMENTS_ONE = 'ASSIGNMENTS_ONE';
const ASSIGNMENTS_DEL = 'ASSIGNMENTS_DEL';
const ATTACHMENT_DEL = 'ASSIGNMENTS_DEL';
const ASSIGNED_ASSIGNMENTS_ALL = 'ASSIGNED_ASSIGNMENTS_ALL';

const state = {
  assignments: [],
  assignedAssignments: [],
};

const mutations = {
  [ASSIGNED_ASSIGNMENTS_ALL] (state, assignments) {
    state.assignedAssignments = _.sortBy(
      assignments, a => new Date(a.myUserTarget.createdDate).getTime()
    );
  },

  [ASSIGNMENTS_ALL] (state, assignments) {
    state.assignments = _.sortBy(
      assignments, a => -new Date(a.createdDate).getTime()
    );
  },

  [ASSIGNMENTS_ONE] (state, assignmentData) {
    const assignment = _.find(state.assignments, s => s.assignmentId === assignmentData.assignmentId);
    if (assignment) {
      _.assign(assignment, assignmentData);
    } else {
      state.assignments.push(assignmentData);
    }
  },

  [ASSIGNMENTS_DEL] (state, assignment) {
    // not using _.pull so Vue detects the change
    const index = _.findIndex(state.assignments, s => s.assignmentId === assignment.assignmentId);
    if (index >= 0) {
      state.assignments.splice(index, 1);
    }
  },

  [ATTACHMENT_DEL] (state, attachment) {
    // not using _.pull so Vue detects the change
    const index1 = _.findIndex(state.assignments, s => s.assignmentId === attachment.assignmentId);
    if (index1 >= 0) {
      _.remove(state.assignments[index1].attachments, s => s.id === attachment.id);
    }
  }
};

const dispatchRecordingResponses = (dispatch, assignments) => {
  if (!assignments || !assignments.length) return;

  assignments.forEach((assignment) => {
    if (assignment.scorecard && assignment.scorecard.responses) {
      dispatch(
        'registerRecordingResponses',
        assignment.scorecard.responses
      );
    }
  });
};

const actions = {
  getAssignedAssignments: ({ commit, dispatch }) => {
    return service.getAssignedAssignments().then(
      (assignments) => {
        dispatchRecordingResponses(dispatch, assignments);
        commit(ASSIGNED_ASSIGNMENTS_ALL, assignments);
      }
    );
  },
  listAssignments: ({ commit, dispatch }) => {
    return service.get().then(
      (assignments) => {
        dispatchRecordingResponses(dispatch, assignments);
        commit(ASSIGNMENTS_ALL, assignments);
      }
    );
  },
  addRecordingToAssignment: ({  }, assignmentArgs) => {
    // Ensure we don't mutate state
    const {assignmentId, recordingId} = assignmentArgs;
    return service.addRecording(assignmentId, recordingId);
  },
  removeRecordingFromAssignment: ({  }, assignmentArgs) => {
    const {assignmentId, recordingId} = assignmentArgs;

    return service.removeRecording(assignmentId, recordingId);
  },
  addAssignment: ({ commit }, assignmentArgs) => {
    // Ensure we don't mutate state
    let assignmentData = _.cloneDeep(assignmentArgs.assignment);

    // Enforce templates based on assignment_template_id
    if (!assignmentData.assignmentTemplateId) {
      assignmentData.isTemplate = true;
    }
    const nullableFields = ['groupId', 'assignmentTemplateId'];
    nullableFields.forEach((f) => {
      if (!assignmentData[f]) {
        delete assignmentData[f];
      }
    });

    return service.add(assignmentData)
      .then((assignment) => {
        commit(ASSIGNMENTS_ONE, assignment);
        return assignment;
      });
  },
  deleteAssignment: ({ commit }, assignment) => {
    return service.delete(assignment.assignmentId)
      .then(() => {
        commit(ASSIGNMENTS_DEL, assignment);
      });
  },
  saveAssignmentScorecard: ({ commit }, {scorecard, assignment}) => {
    const assignmentState = _.cloneDeep(_.find(state.assignments, s => s.assignmentId === assignment.assignmentId));
    if (!assignmentState) {
      throw new Error('Assignment not found');
    }
    const action = (!assignmentState.scorecard) ?
      'create' : 'update';
    return scorecardService[action](scorecard)
      .then((_scorecard) => {
        assignmentState.scorecard = _scorecard;
        commit(ASSIGNMENTS_ONE, assignmentState);
      });
  },
  deleteAttachment: ({ commit }, attachment) => {
    return service.deleteAttachment(attachment.id)
      .then(() => {
        commit(ATTACHMENT_DEL, attachment);
      });
  },
  updateAssignment: ({ commit, dispatch }, assignmentArgs) => {
    // Ensure we don't mutate state
    let assignmentData = _.cloneDeep(assignmentArgs.assignment);
    let newAttachments = assignmentArgs.newAttachments || [];
    let forceUpdate = false;

    delete assignmentData.isFavorite;
    if (!assignmentArgs.updateWordspotting) {
      delete assignmentData.wordspotting;
    }
    if (!assignmentArgs.updateArchived) {
      delete assignmentData.archived;
    }

    ['groupId', 'assignmentTemplateId'].forEach((field) => {
      if (assignmentData[field] === null) delete assignmentData[field];
    });

    if (!assignmentArgs.updateTargets) {
      delete assignmentData.userTargets;
      delete assignmentData.groupTargets;
    }

    // Only submit new attachments (based on URL/recordingId)
    const existingAttachments = (assignmentData.attachments) ?
      assignmentData.attachments.map((a) => {
        return (a.attachmentType === 'link') ? a.attachmentUrl :
          (a.attachmentType === 'recording') ? a.recordingId : null;
      }) :
      [];
    assignmentData.attachments = [];
    newAttachments.forEach((a) => {
      const identifier = a.recordingId || a.attachmentUrl;
      if (identifier && existingAttachments.indexOf(identifier) === -1) {
        forceUpdate = true;
        assignmentData.attachments.push(a);
      }
    });

    return service.update(assignmentData.assignmentId, assignmentData)
      .then((_assignmentData) => {
        commit(ASSIGNMENTS_ONE, _assignmentData);
        if (forceUpdate) {
          return dispatch('listAssignments');
        }
      });
  },
};

const getters = {
  assignments: state => state.assignments,
  assignedAssignments: state => state.assignedAssignments,
};

export default {
  state,
  mutations,
  actions,
  getters
};
